package dev.danko.roblox_craft_ai.components

import dev.danko.roblox_craft_ai.state.Txt
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.danko.roblox_craft_ai.client.RobloxCraftApiClient
import dev.danko.roblox_craft_ai.pages.generateSessionToken
import dev.danko.roblox_craft_ai.shared.model.AssetType
import dev.danko.roblox_craft_ai.shared.model.ProviderConfigDto
import dev.danko.roblox_craft_ai.shared.model.TestKeyResponse
import dev.danko.roblox_craft_ai.shared.model.UserSettingsDto
import dev.danko.roblox_craft_ai.uikit.components.AppButton
import dev.danko.roblox_craft_ai.uikit.components.AppButtonSize
import dev.danko.roblox_craft_ai.uikit.components.AppButtonVariant
import dev.danko.roblox_craft_ai.uikit.components.ModalDialog
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import dev.danko.roblox_craft_ai.uikit.theme.toAppPalette
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.TextArea

private const val DEFAULT_MODEL_KEY = "robloxCraft.defaultModel"
private const val DEFAULT_TYPE_KEY = "robloxCraft.defaultAssetType"
private const val TEMPERATURE_KEY = "robloxCraft.temperature"
private const val MAX_TOKENS_KEY = "robloxCraft.maxTokens"

fun loadSavedDefaultModelOrNull(): String? {
    return window.asDynamic().localStorage.getItem(DEFAULT_MODEL_KEY) as? String
}

fun saveDefaultModel(modelId: String?) {
    if (modelId != null) {
        window.asDynamic().localStorage.setItem(DEFAULT_MODEL_KEY, modelId)
    } else {
        window.asDynamic().localStorage.removeItem(DEFAULT_MODEL_KEY)
    }
}

fun loadSavedDefaultType(): AssetType {
    val saved = window.asDynamic().localStorage.getItem(DEFAULT_TYPE_KEY) as? String
    return try {
        if (saved != null) AssetType.valueOf(saved) else AssetType.LUAU_SCRIPT
    } catch (_: Throwable) {
        AssetType.LUAU_SCRIPT
    }
}

fun saveDefaultType(type: AssetType) {
    window.asDynamic().localStorage.setItem(DEFAULT_TYPE_KEY, type.name)
}

fun loadSavedTemperature(): Double {
    val saved = window.asDynamic().localStorage.getItem(TEMPERATURE_KEY) as? String
    return saved?.toDoubleOrNull() ?: 0.3
}

fun saveTemperature(temp: Double) {
    window.asDynamic().localStorage.setItem(TEMPERATURE_KEY, temp.toString())
}

fun loadSavedMaxTokens(): Int {
    val saved = window.asDynamic().localStorage.getItem(MAX_TOKENS_KEY) as? String
    return saved?.toIntOrNull() ?: 4096
}

fun saveMaxTokens(tokens: Int) {
    window.asDynamic().localStorage.setItem(MAX_TOKENS_KEY, tokens.toString())
}

val SettingsTabStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier
        .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
        .borderRadius(0.5.cssRem)
        .fontSize(0.875.cssRem)
        .fontWeight(FontWeight.Medium)
        .cursor(Cursor.Pointer)
        .userSelect(UserSelect.None)
        .transition(
            Transition.of("background-color", 150.ms),
            Transition.of("color", 150.ms)
        )
}

val SettingsTabActiveStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier
        .backgroundColor(palette.surfaceElevated)
        .color(palette.accent)
        .fontWeight(FontWeight.SemiBold)
        .border(1.px, LineStyle.Solid, palette.border)
}

val SettingsCardStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier
        .fillMaxWidth()
        .padding(1.cssRem)
        .backgroundColor(palette.surfaceElevated)
        .borderRadius(0.75.cssRem)
        .border(1.px, LineStyle.Solid, palette.border)
}

val SettingsInputStyle = CssStyle {
    base {
        val palette = colorMode.toAppPalette()
        Modifier
            .fillMaxWidth()
            .minHeight(2.25.cssRem)
            .padding(topBottom = 0.4375.cssRem, leftRight = 0.75.cssRem)
            .backgroundColor(palette.surface)
            .color(palette.textPrimary)
            .border(1.px, LineStyle.Solid, palette.border)
            .borderRadius(0.375.cssRem)
            .fontSize(0.875.cssRem)
            .fontFamily("ui-monospace", "SFMono-Regular", "Menlo", "monospace")
            .outline { width(0.px) }
            .transition(
                Transition.of("border-color", 150.ms),
                Transition.of("box-shadow", 150.ms)
            )
    }
    cssRule(":focus") {
        val palette = colorMode.toAppPalette()
        Modifier
            .border(2.px, LineStyle.Solid, palette.accent)
            .boxShadow(0.px, 0.px, 0.px, 3.px, palette.accent.toRgb().copy(alpha = 38))
    }
}

@Deprecated(
    message = "Используйте route /settings вместо модалки. UserSettingsModal будет удалён в следующих релизах.",
    replaceWith = ReplaceWith("window.location.href = \"/settings\"")
)
@Composable
fun UserSettingsModal(
    visible: Boolean,
    onCloseRequest: () -> Unit,
    sessionToken: String,
    onSessionTokenChanged: (String) -> Unit,
    connected: Boolean,
    onSettingsSaved: (List<ProviderConfigDto>) -> Unit = {}
) {
    if (!visible) return
    LaunchedEffect(visible) {
        delay(150)
        window.location.href = "/settings"
        onCloseRequest()
    }
    ModalDialog(
        visible = true,
        title = Txt.settingsRedirect.redirecting,
        onCloseRequest = onCloseRequest
    ) {
        val theme = LocalAppTheme.current
        Column(
            Modifier
                .fillMaxWidth()
                .padding(theme.spacing.md)
                .gap(theme.spacing.sm),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            SpanText("🔀", Modifier.fontSize(2.cssRem))
            SpanText(
                Txt.settingsRedirect.openingNewPage,
                Modifier.fontWeight(FontWeight.SemiBold).color(theme.colors.textPrimary).fontSize(1.cssRem)
            )
            SpanText(
                Txt.settingsRedirect.movedNotice,
                Modifier.color(theme.colors.textSecondary).fontSize(0.8.cssRem)
            )
            Link("/", variant = UndecoratedLinkVariant) {
                AppButton(
                    text = Txt.settingsRedirect.openSettings,
                    iconLeft = "⚙️",
                    variant = AppButtonVariant.PRIMARY,
                    size = AppButtonSize.MD,
                    onClick = { window.location.href = "/settings" }
                )
            }
        }
    }
    return
    @Suppress("UNREACHABLE_CODE", "UNUSED_VARIABLE", "unused")
    val neverUsed: Boolean = run {
        val theme = LocalAppTheme.current
        val scope = rememberCoroutineScope()
        var selectedTab by remember { mutableStateOf(0) }
        var glmApiKeyInput by remember { mutableStateOf("") }
        var geminiApiKeyInput by remember { mutableStateOf("") }
        var clearGlmKeyFlag by remember { mutableStateOf(false) }
        var clearGeminiKeyFlag by remember { mutableStateOf(false) }
        var showGlmKey by remember { mutableStateOf(false) }
        var showGeminiKey by remember { mutableStateOf(false) }
        var providers by remember { mutableStateOf<List<ProviderConfigDto>>(emptyList()) }
        var isLoadingProviders by remember { mutableStateOf(true) }
        var glmTestResult by remember { mutableStateOf<TestKeyResponse?>(null) }
        var geminiTestResult by remember { mutableStateOf<TestKeyResponse?>(null) }
        var isTestingGlm by remember { mutableStateOf(false) }
        var isTestingGemini by remember { mutableStateOf(false) }
        var defaultModel by remember { mutableStateOf(loadSavedDefaultModelOrNull()) }
        var defaultType by remember { mutableStateOf(loadSavedDefaultType()) }
        var temperature by remember { mutableStateOf(loadSavedTemperature()) }
        var maxTokens by remember { mutableStateOf(loadSavedMaxTokens()) }
        var skipConfirm by remember { mutableStateOf(isSkipSyncConfirmEnabled()) }
        var currentToken by remember { mutableStateOf(sessionToken) }
        var isSaving by remember { mutableStateOf(false) }
        var saveSuccess by remember { mutableStateOf(false) }
        var saveError by remember { mutableStateOf<String?>(null) }
        var showImportBox by remember { mutableStateOf(false) }
        var importJsonText by remember { mutableStateOf("") }
        var importError by remember { mutableStateOf<String?>(null) }
        false
    }
}
